import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ClubbieTable from '../../components/ClubbieTable/ClubbieTable';
import { config } from '../../utils/config';
import ClubbieLoader from '../../components/Loader/ClubbieLoader';

const PaymentCardDetails = () => {
  const navigate = useNavigate();
  const { paymentId } = useParams();

  const [loading, setLoading] = useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [user, setUser] = useState(null);
  const [payments, setPayments] = useState([]);
  const [selectedPayments, setSelectedPayments] = useState([]);

  useEffect(() => {
    fetchUserData();
    fetchPayments();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/auth/user`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch user');

      const data = await response.json();
      setUser(data);

      if (data.stripe?.customer?.paymentMethods) {
        setPaymentMethods(data.stripe.customer.paymentMethods);
        if (data.stripe.customer.defaultPayMethodId) {
          setSelectedPaymentMethod(data.stripe.customer.defaultPayMethodId);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments?status=unpaid&status=pending`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch payments');

      const data = await response.json();
      setPayments(data.results || []);

      if (paymentId) {
        const targetPayment = data.results.find(p => p.id === paymentId);
        if (targetPayment) {
          setSelectedPayments([targetPayment.id]);
        }
      }
    } catch (error) {
    }
  };

  const handleTogglePayment = (paymentId) => {
    setSelectedPayments(prev =>
      prev.includes(paymentId)
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const handleAddPaymentMethod = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/auth/stripe/payment-method/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/payments/card-details`,
          cancelUrl: `${window.location.origin}/payments/card-details`
        })
      });

      if (!response.ok) throw new Error('Failed to create payment method session');

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      alert('Failed to add payment method. Please try again.');
    }
  };

  const handleRemovePaymentMethod = async (methodId) => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/auth/stripe/payment-method/${methodId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Failed to remove payment method');

      await fetchUserData();
      alert('Payment method removed successfully');
    } catch (error) {
      alert('Failed to remove payment method. Please try again.');
    }
  };

  const handleSetDefaultPaymentMethod = async (methodId) => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/auth/stripe/payment-method/default`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ paymentMethodId: methodId })
      });

      if (!response.ok) throw new Error('Failed to set default payment method');

      setSelectedPaymentMethod(methodId);
      await fetchUserData();
    } catch (error) {
      alert('Failed to set default payment method. Please try again.');
    }
  };

  const handleProceedToPayment = async () => {
    if (!selectedPaymentMethod || selectedPayments.length === 0) {
      alert('Please select a payment method and at least one payment');
      return;
    }

    // SKIP STRIPE REDIRECT IN DEVELOPMENT
    if (process.env.NODE_ENV === 'development' || true) {
      navigate('/payments?success=true');
      return;
    }

    try {
      const selectedPaymentData = payments.filter(p => selectedPayments.includes(p.id));
      const teamId = selectedPaymentData[0]?.team?.id;

      if (!teamId) {
        alert('Invalid payment data');
        return;
      }

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/stripe/payment-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          teamId,
          paymentIds: selectedPayments,
          successUrl: `${window.location.origin}/payments?success=true`,
          cancelUrl: `${window.location.origin}/payments/card-details/${paymentId || ''}`
        })
      });

      if (!response.ok) throw new Error('Failed to create payment session');

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      alert('Failed to process payment. Please try again.');
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'description',
        header: () => <span className="text-[#FEFCEB]">Description</span>,
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={selectedPayments.includes(row.original.id)}
              onChange={() => handleTogglePayment(row.original.id)}
              className="w-5 h-5 rounded border-2 border-[#192634] bg-transparent checked:bg-[#00E7FF] checked:border-[#00E7FF] cursor-pointer"
            />
            {row.original.team?.crest && (
              <div
                className="w-8 h-8 rounded-full bg-cover bg-center bg-white"
                style={{ backgroundImage: `url(${row.original.team.crest})` }}
              />
            )}
            <span className="text-[#FEFCEB]">
              {row.original.payFor === 'Event'
                ? row.original.payReference?.title || 'Event Payment'
                : row.original.payFor === 'Subscription'
                  ? 'Subscription Payment'
                  : row.original.customReference || 'Custom Payment'}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'amount',
        header: () => <span className="text-[#FEFCEB]">Amount</span>,
        cell: ({ row }) => (
          <span className="text-[#FEFCEB] font-medium">£{row.original.amount.toFixed(2)}</span>
        ),
      },
    ],
    [selectedPayments]
  );

  const total = useMemo(() => {
    return payments
      .filter(p => selectedPayments.includes(p.id))
      .reduce((sum, p) => sum + p.amount, 0);
  }, [payments, selectedPayments]);

  const getCardBrandIcon = (brand) => {
    const brandLower = brand?.toLowerCase();
    if (brandLower?.includes('visa')) {
      return <span className="text-[#FEFCEB] font-bold text-xs">VISA</span>;
    }
    if (brandLower?.includes('amex') || brandLower?.includes('american')) {
      return <span className="text-[#FEFCEB] font-bold text-xs">AMEX</span>;
    }
    if (brandLower?.includes('master')) {
      return (
        <div className="flex gap-0.5">
          <div className="w-4 h-4 rounded-full bg-red-600" />
          <div className="w-4 h-4 rounded-full bg-orange-400" />
        </div>
      );
    }
    return <span className="text-[#FEFCEB] font-bold text-xs">{brand}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000F1F] flex items-center justify-center">
        <ClubbieLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000F1F] text-[#FEFCEB]">
      <div className="max-w-[1200px] mx-auto py-6 px-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/payments')}
            className="text-[#7F8585] hover:text-[#FEFCEB] transition-colors"
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-[#FEFCEB] m-0">Payment Details</h1>
        </div>

        {payments.length > 0 && (
          <div className="bg-[#011224] rounded-2xl border border-[#192634] p-6 mb-6">
            <h2 className="text-xl font-semibold text-[#FEFCEB] mb-4">Select Payments</h2>
            <ClubbieTable
              data={payments}
              columns={columns}
              pageSize={10}
              showPageSize={false}
            />
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#192634]">
              <span className="text-[#FEFCEB] font-semibold text-lg">Total</span>
              <span className="text-[#00E7FF] font-bold text-2xl">£{total.toFixed(2)}</span>
            </div>
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[#FEFCEB] mb-2">Payment methods</h3>
          <p className="text-[#7F8585] text-sm mb-4">
            Select a payment method to complete your transaction
          </p>

          <div className="bg-[#011224] rounded-2xl border border-[#192634] p-6 space-y-3">
            {paymentMethods.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#7F8585] mb-4">No payment methods added yet</p>
              </div>
            ) : (
              paymentMethods.map((method) => (
                <div
                  key={method.id}
                  onClick={() => handleSetDefaultPaymentMethod(method.id)}
                  className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-colors ${selectedPaymentMethod === method.id
                    ? 'border-[#00E7FF] bg-[#082441]'
                    : 'border-[#192634] bg-[#000F1F] hover:border-[#7F8585]'
                    }`}
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="radio"
                      checked={selectedPaymentMethod === method.id}
                      onChange={() => { }}
                      className="w-5 h-5"
                    />

                    <div className="w-16 h-11 bg-[#5FC4EE] rounded flex items-center justify-center">
                      {getCardBrandIcon(method.brand)}
                    </div>

                    <div>
                      <p className="text-[#FEFCEB] font-medium">
                        {method.brand}...{method.last4}
                      </p>
                      <p className="text-[#7F8585] text-sm">
                        Expires {method.expMonth}/{method.expYear}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Are you sure you want to remove this payment method?')) {
                        handleRemovePaymentMethod(method.id);
                      }
                    }}
                    className="text-[#7F8585] hover:text-[#FEFCEB] transition-colors text-xl"
                  >
                    ✕
                  </button>
                </div>
              ))
            )}

            <button
              onClick={handleAddPaymentMethod}
              className="flex items-center gap-2 text-[#00E7FF] hover:text-[#00d4e6] transition-colors p-4 w-full"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="text-[#00E7FF]"
              >
                <path
                  d="M10 4v12m-6-6h12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span>Add a new payment method</span>
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          {(process.env.NODE_ENV === 'development' || true) && (
            <button
              onClick={() => navigate('/payments?success=true')}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-full font-bold transition-colors"
            >
              DEBUG: Mock Success
            </button>
          )}
          <button
            onClick={handleProceedToPayment}
            disabled={!selectedPaymentMethod || selectedPayments.length === 0}
            className="bg-[#00E7FF] hover:bg-[#00d4e6] text-[#000F1F] px-8 py-3 rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Proceed to Payment (£{total.toFixed(2)})
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentCardDetails;
